<template>
  <v-dialog
    v-model="show"
    :max-width="dialogWidth"
    scrollable
  >
    <v-card max-height="90vh">
      <v-card-title>
        Move To
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          label="Search"
          dense
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
        ></v-text-field>
        <v-btn icon @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle class="pb-3 pt-1">
        <v-checkbox
          v-model="showCompletedFolders"
          dense
          hide-details
          @change="changeShowCompleted"
          label="Show 'Completed' Folders/Tasks"
        ></v-checkbox>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-treeview
          v-if="target && treeItems.length"
          :items="treeItems"
          :search="search"
          :active.sync="active"
          return-object
          item-text="Name"
          :load-children="fetchTasksAndFolders"
          item-disabled="disabled"
          activatable
          color="success"
          dense
          item-children="Tasks"
          item-key="Item_ID"
        >
          <template v-slot:prepend="{ item, open }">
            <template v-if="['Project', 'Folder'].indexOf(item.Type) > -1">
              <div class="mr-2">
                <v-icon color="primary" v-if="['integrated', 'integrated-employee'].indexOf(item.Jira_Task_Key) > -1">
                  mdi-atlassian mdi-18px
                </v-icon>
                <v-icon
                  :color="item.Type === 'Folder' && item.Status === 'Completed' ? 'blue-grey lighten-3' : 'primary'"
                >
                  {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                </v-icon>
              </div>
            </template>
            <v-icon class="mr-2" color="success" v-else>
              mdi-file-code
            </v-icon>
          </template>
        </v-treeview>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn small color="primary" outlined @click="show = false">cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn small color="primary" :disabled="active.length === 0" @click="moveTo">move</v-btn>
      </v-card-actions>
    </v-card>
    <ConfirmOrDeniedDialog
      v-model="showDeniedDialog"
      v-if="showDeniedDialog"
      :mode="confirmOrDeniedDialogModes.denied"
      :title="deniedTitle"
      :message="deniedMessage"
    />
  </v-dialog>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
export default {
  name: 'MoveToDialog',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: true
    },
    target: {
      required: false
    },
    listOfProjectsAndTasks: {
      required: false
    },
    isShowCompletedChecked: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      treeItems: [],
      fullProjectList: [],
      active: [],
      search: null,
      deniedTitle: '',
      deniedMessage: '',
      showDeniedDialog: false,
      showCompletedFolders: false
    }
  },
  mounted () {
    if (this.target && this.listOfProjectsAndTasks && this.listOfProjectsAndTasks.length) {
      this.showCompletedFolders = this.isShowCompletedChecked
      this.fullProjectList = JSON.parse(JSON.stringify(this.listOfProjectsAndTasks))
      this.treeItems = [...this.preparedNewItems(JSON.parse(JSON.stringify(this.fullProjectList)))]
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        if (!value) {
          this.$emit('closed')
        }
        this.$emit('input', value)
      }
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    dialogWidth () {
      return this.$vuetify.breakpoint.mdAndDown ? '90vw' : '50vw'
    }
  },
  methods: {
    moveTo () {
      if (this.active[0].Type !== 'Project' && this.target.Status !== this.active[0].Status) {
        this.deniedTitle = 'Moving Task'
        this.deniedMessage = 'Tasks or folder status is not the same as the destination folder'
        this.showDeniedDialog = true
        return
      }
      const targetTaskID = this.target.Task_ID
      const toProjectID = this.active[0].Project_ID
      const toTaskID = this.active[0].Task_ID || null

      this.axios.post('/api/tasks/reps/move-task', {
        Task_ID: targetTaskID,
        To_Project_ID: toProjectID,
        To_Task_ID: toTaskID
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Successfully moved' })
        this.$emit('refreshProjects', [this.target.Project_ID, toProjectID])
        this.show = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async fetchTasksAndFolders (e) {
      if (e.Type === 'Project') {
        await this.axios.post('/api/tasks-and-folders-list', { Project_ID: e.Project_ID }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          const clickedProjectInFullList = this.fullProjectList.find(project => project.Project_ID === e.Project_ID)
          const clickedProjectInTreeList = this.treeItems.find(project => project.Project_ID === e.Project_ID)
          clickedProjectInFullList.Tasks = res.data
          clickedProjectInTreeList.Tasks.push(...this.preparedNewItems(clickedProjectInFullList.Tasks))
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    changeShowCompleted (e) {
      this.showCompletedFolders = e
      if (!this.showCompletedFolders && this.active.length && this.active[0].Status === 'Completed') {
        this.active = []
      }
      this.treeItems = [...this.preparedNewItems(JSON.parse(JSON.stringify(this.fullProjectList)))]
    },
    preparedNewItems (items) {
      const newItems = []
      for (let i = 0; i < items.length; i++) {
        if (items[i].Type === 'Task') {
          continue
        }
        if (!this.showCompletedFolders && items[i].Status === 'Completed') {
          continue
        }
        if (items[i].Item_ID === this.target.Item_ID) {
          items[i].disabled = true
          delete items[i].Tasks
          newItems.push(items[i])
          continue
        }
        if (items[i].Tasks && items[i].Tasks.length) {
          items[i].Tasks = [...this.preparedNewItems(items[i].Tasks)]
          if (items[i].Tasks.length === 0) {
            delete items[i].Tasks
          }
        }
        newItems.push(items[i])
      }
      return newItems
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep {
  .v-treeview-node__root {
    cursor: pointer;
  }
}
</style>
